import { FC, useEffect, useLayoutEffect, useState } from "react";
import { Alert, FormControl, FormLabel, Grid, MenuItem } from "@mui/material";
import { makeStyles } from "@mui/styles";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import { Select } from "../../createBadge/form/components";
import { useSelector } from "react-redux";
import { productsSelector } from "../../../redux/magento/selectors/magento.selectors";
import productsUtils from "../../../common/utils/productsUtils";
import { useTranslation } from "react-i18next";
import { SelectChangeEvent } from "@mui/material/Select";
import colors from "../../../common/styles/colors";
import { useFreyaSetup } from "../../freya/hooks/useFreyaSetup";
import { OrientationToggle } from "../orientation/OrientationToggle";
import { FREYA_ORIENTATION_KEY } from "../../../common/constants";
import { FreyaPrintSettingsDeviceSelector } from "./FreyaPrintSettingsDeviceSelector";
import { useCameraScanner } from "./useCameraScanner";
import { ScannerCheckbox } from "./scannerCheckbox/ScannerCheckbox";
import EventHelper from "../../../common/network/helpers/event/EventHelper";
import { useColocatedShows } from "../../globalSettings/hooks/useColocatedShows";
import { ColocatedShows } from "../../globalSettings/colocatedShows/ColocatedShows";
import { useGetFreyaBadges } from "../../freya/hooks/useGetFreyaBadges";
import { useDispatch } from "react-redux";
import {
  colocatedShowDataSyncCompleted,
  colocatedShowDataSyncFailed,
  colocatedShowDataSyncProgressUpdated,
  colocatedShowDataSyncStarted
} from "../../../redux/metadata/slice/metadataSlice";
import { colocatedShowDataSyncStatusSelector } from "../../../redux/metadata/selectors/metadata.selectors";
import { ColocatedShowDataSyncStatus } from "../../../common/enums";
import { useSnackbar } from "notistack";
import { SNACKBAR_VARIANTS } from "../../../common/constants";

export const FREYA_DEVICE_ID_KEY = "DEVICE_ID";

const useStyles = makeStyles({
  container: {
    marginTop: 40,
    justifyContent: "space-between",
    display: "flex",
    flexDirection: "column"
  },
  select: {
    width: "100%"
  },
  reloadIcon: {
    transform: "scaleX(-1) rotate(-45deg)",
    color: colors.primary,
    cursor: "pointer"
  },
  inputLabel: {
    fontWeight: 600,
    fontSize: 14,
    color: colors.darkGrey1,
    marginBottom: 7
  },
  titleIcon: {
    marginRight: 10
  },
  title: {
    marginBottom: 15,
    fontWeight: 400,
    fontSize: 20,
    color: colors.darkGrey1,
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center"
  },
  subtitle: {
    marginBottom: 22,
    fontWeight: 400,
    fontSize: 14,
    color: colors.lightGrey10
  },
  alert: {
    marginBottom: 32,
    fontWeight: 600,
    fontSize: 14,
    letterSpacing: -0.441,
    color: colors.brownColor
  },
  inputsTitle: {
    color: colors.greenColor1,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase"
  },
  selectGrid: {
    marginBottom: 0,
    marginTop: -12
  },
  selectTest: {
    top: "367px !important",
    transform: "rotate(180deg) !important"
  }
});

interface Props {
  setSaveDisabled: (state: boolean) => void;
}

export const FreyaPrintSettings: FC<Props> = ({ setSaveDisabled }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedZone, setSelectedZone] = useState<string>("");
  const [isSyncingColocatedBadges, setIsSyncingColocatedBadges] = useState(false);
  const colocatedShowDataSyncStatus = useSelector(colocatedShowDataSyncStatusSelector);
  const isColocatedShowDataSyncFailed = colocatedShowDataSyncStatus === ColocatedShowDataSyncStatus.FAILED;
  const classes = useStyles();
  const products = useSelector(productsSelector);
  const zoneLocations = productsUtils.getValidZones(products);
  const { t } = useTranslation("common");
  const eventEdition = EventHelper.eventEdition;
  const { fetchBadges, startBadgeFetchInterval } = useGetFreyaBadges();
  const {
    colocatedShows,
    selectedShows,
    onToggleShow,
    shouldColocatedUpdate,
    saveColocatedShows,
    loading: colocatedLoading
  } = useColocatedShows(eventEdition?.id || "");

  const { cameraDevices, selectedCameraDevice, initCameraDevices, setDeviceIdToIndexDB, setCamera } = useCameraScanner({
    useIndexDB: true
  });
  const { checkProducts, zone } = useFreyaSetup();

  const preselectZone = async () => {
    const cachedZoneId = await zone.get();
    if (cachedZoneId && zoneLocations.find(zoneLocation => zoneLocation.id === +cachedZoneId)) {
      setSelectedZone(cachedZoneId);
      return;
    }
    if (!selectedZone && zoneLocations[0]) {
      setSelectedZone(zoneLocations[0].id as unknown as string);
    }
  };

  useLayoutEffect(() => {
    checkProducts();
  }, []);

  useEffect(() => {
    preselectZone();
  }, [zoneLocations]);

  const onZoneChange = (event: SelectChangeEvent<string>) => {
    setSelectedZone(event.target.value);
    zone.set(event.target.value);
  };

  const onDeviceChange = async (event: SelectChangeEvent<string>) => {
    const selectedCamera = cameraDevices.find(device => device.id === event.target.value)!;
    await setCamera(selectedCamera);
    await setDeviceIdToIndexDB(event.target.value);
  };

  useEffect(() => {
    setSaveDisabled(Boolean(!selectedZone || !selectedCameraDevice || isSyncingColocatedBadges || isColocatedShowDataSyncFailed));
  }, [selectedZone, selectedCameraDevice, isSyncingColocatedBadges, isColocatedShowDataSyncFailed]);

  useEffect(() => {
    initCameraDevices();
  }, []);

  const onSaveColocatedShows = async () => {
    if (!eventEdition?.gbsCode) {
      return;
    }

    setIsSyncingColocatedBadges(true);
    dispatch(colocatedShowDataSyncStarted());

    try {
      await saveColocatedShows();
      dispatch(colocatedShowDataSyncProgressUpdated(33));

      const colocatedGbsCodes = selectedShows.map(show => show.gbsCode).filter(Boolean);
      const eventGbsCodes = Array.from(new Set([eventEdition.gbsCode, ...colocatedGbsCodes]));

      await fetchBadges(eventGbsCodes);
      dispatch(colocatedShowDataSyncProgressUpdated(100));
      dispatch(colocatedShowDataSyncCompleted());
      startBadgeFetchInterval(eventGbsCodes);
    } catch (e) {
      dispatch(colocatedShowDataSyncFailed());
      enqueueSnackbar(<b>{t("header.colocatedShowDataSync.failedTooltip")}</b>, SNACKBAR_VARIANTS.error);
      console.error(e);
    } finally {
      setIsSyncingColocatedBadges(false);
    }
  };

  return (
    <div className={classes.container} key={localStorage.getItem(FREYA_ORIENTATION_KEY)}>
      <div className={classes.title}>
        <QrCodeScannerIcon className={classes.titleIcon} />
        <div>{t("kioskPrintSettings.title")}</div>
      </div>
      <div className={classes.subtitle}>{t("kioskPrintSettings.subtitle")}</div>
      <Alert className={classes.alert} severity="warning">
        {t("kioskPrintSettings.alert")}
      </Alert>
      <div>
        <ScannerCheckbox />

        <ColocatedShows
          colocatedShows={colocatedShows}
          selectedShows={selectedShows}
          onToggleShow={onToggleShow}
          loading={colocatedLoading}
          eventEditionName={eventEdition?.name || undefined}
          onSaveChanges={onSaveColocatedShows}
          saveChangesDisabled={!shouldColocatedUpdate || isSyncingColocatedBadges}
          saveChangesLabel={t("common.buttonLabels.saveChanges")}
        />
        <FormLabel className={classes.inputsTitle}>{t("kioskPrintSettings.inputTitle")}</FormLabel>
        <Grid className={classes.selectGrid} container alignItems="flex-start" spacing={2} sx={{ mb: 4 }}>
          <FreyaPrintSettingsDeviceSelector
            cameraDevices={cameraDevices}
            selectedCameraDevice={selectedCameraDevice}
            getAvailableCameraDevices={initCameraDevices}
            onDeviceChange={onDeviceChange}
          />
          <Select
            renderIcon={() => <FormControl size="small" className={classes.select} />}
            label={t("kioskPrintSettings.zoneLocationSelect")}
            name={t("kioskPrintSettings.zoneLocationSelect")}
            value={selectedZone}
            onChange={onZoneChange}
            native={false}
          >
            {zoneLocations &&
              zoneLocations.map(zone => (
                <MenuItem key={zone.id} value={zone.id}>
                  {zone.label}
                </MenuItem>
              ))}
          </Select>
        </Grid>
        <OrientationToggle />
      </div>
    </div>
  );
};
